<script setup>
import { RouterView } from 'vue-router'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AppNavigation from './components/AppNavigation.vue'
import AppLoading from './components/AppLoading.vue'
import AppFooter from './components/AppFooter.vue'
import { useUserStore } from '@/stores/user'
import { userSettingsAPI } from '@/services/api'
import VerifyEmailAlert from './components/VerifyEmailAlert.vue'

const userStore = useUserStore()
const { locale } = useI18n()
const initialLoading = ref(true)

// Setup language based on user preferences or browser detection
const setupLanguage = async () => {
  let preferredLanguage = null
  let shouldSaveToServer = false
  
  // Load from localStorage first for immediate UI feedback
  const localStorageLanguage = localStorage.getItem('user-language')
  if (localStorageLanguage) {
    locale.value = localStorageLanguage
    userStore.setLanguage(localStorageLanguage)
  }
  
  // 1. If user is authenticated, their language preference should already be available
  // (this will be handled when we properly integrate user settings with user data)
  
  // 2. If no localStorage, detect from browser
  if (!localStorageLanguage) {
    const browserLang = navigator.language || navigator.userLanguage
    const langCode = browserLang.split('-')[0].toLowerCase()
    
    // Check if detected language is supported
    const supportedLanguages = ['en', 'it']
    preferredLanguage = supportedLanguages.includes(langCode) ? langCode : 'en'
    shouldSaveToServer = true // We detected from browser, save to server
  } else {
    preferredLanguage = localStorageLanguage
  }
  
  // Apply the determined language
  locale.value = preferredLanguage
  userStore.setLanguage(preferredLanguage)
  localStorage.setItem('user-language', preferredLanguage)
  
  // Save browser-detected language to server if user is logged in
  if (shouldSaveToServer && userStore.isAuthenticated) {
    try {
      await userSettingsAPI.updateSettings({
        language: preferredLanguage,
        results_notifications: true, // Default values
        updates_notifications: true
      })
    } catch (error) {
      console.warn('Could not save language preference to server:', error)
    }
  }
}

// Initialize user and language on app mount
onMounted(async () => {
  userStore.initializeLanguage()
  await userStore.initializeUser()
  await setupLanguage()
  userStore.loadFromLocalStorage()
  initialLoading.value = false
})
</script>

<template>
  <v-app>
    <!-- Loading Screen -->
    <AppLoading :show="initialLoading" />

    <!-- Main App Content -->
    <v-fade-transition>
      <div v-if="!initialLoading" class="app-content">
        <AppNavigation />
        <v-main>
          <VerifyEmailAlert />
          <RouterView />
        </v-main>
        <AppFooter />
      </div>
    </v-fade-transition>
  </v-app>
</template>

<style scoped>
.app-content {
  min-height: 100vh;
}

/* Fade transition for main app content */
.v-fade-transition-enter-active,
.v-fade-transition-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.v-fade-transition-enter-from,
.v-fade-transition-leave-to {
  opacity: 0;
}
</style>


